import { useEffect, useMemo } from 'react';
import { type PRsByExercise } from '@heraion/shared';

import { useAsyncResource } from './useAsyncResource';
import { asyncStorageResourceCache } from './state/asyncListCache';
import { computeRetroactivePrStamps } from './prDetect';
import type { LoggerDispatch, LoggerDraft } from './state/loggerDraft';
import type { LoggerHostDeps } from './hostDeps';

// The PR comparators the logger needs and the one effect that repairs a late
// arrival — extracted from useLoggerScreen (500-line cap). One concept: the
// records/maxes the set pill is compared AGAINST. The pure detection math
// stays in prDetect.ts; this file is only the stateful glue around it.
// Called at the exact position it occupied inline, so hook order (and the
// order of the two effects) is unchanged.
export function usePrRecordsResource({
  visible,
  draft,
  dispatch,
  deps,
}: {
  visible: boolean;
  draft: LoggerDraft;
  dispatch: LoggerDispatch;
  deps: LoggerHostDeps;
}) {
  // Per-rep-max history for the click-time PR check on the set pill.
  // Source is the server-computed /api/me/records aggregate — the same
  // endpoint ProgressScreen consumes. Cached in AsyncStorage so a cold
  // boot at the gym shows last-known records before the network call
  // returns; the SWR refresh runs in the background. Scope is fixed to
  // "all" (not "year") so a PR set rendered live matches the all-time
  // records the Progress tab ultimately surfaces.
  //
  // 1.99.x: fetch whenever the Logger is visible. The original
  // condition skipped fetch in edit/repeat mode, but `repeat` opens
  // a NEW session (records is a legitimate comparator) and even the
  // edit case is fine — re-saving an existing set can't beat itself,
  // so no false PR fires. The 1.97.10 diagnostic confirmed this gate was
  // the path that left prHistory null on every tap.
  const recordsResource = useAsyncResource<PRsByExercise>({
    key: 'logger/records',
    fetch: async () => {
      const res = await deps.apiFetch<{ records: PRsByExercise; scope: 'all' }>(
        '/api/me/records',
      );
      return res.records;
    },
    cache: useMemo(
      () => asyncStorageResourceCache<PRsByExercise>('logger/records'),
      [],
    ),
    immediate: visible,
  });
  const prHistory = recordsResource.value;
  // Stored MAX + setAt per exercise → the bidirectional MAX-regression ⬇️
  // marker (next/measurement-contract.md «Il MAX dinamico: il come»). Same source the post-save MAX-bump pill
  // reads; `maxMeta` adds the setAt the staleness gate needs.
  const { maxMeta, maxesLoaded } = deps;
  // Records-arrive-late safety net (1.99.x — inbox 74017a4d). When the
  // PR history loads after the user already tapped pending→done pills,
  // retroactively stamp prFlash on any set that would have flashed.
  // Pure computation lives in computeRetroactivePrStamps; this effect
  // only wires it to the dispatch.
  //
  // ⚠️ The dep array is [prHistory] ON PURPOSE and the eslint-disable below
  // is not an oversight: the effect must run when the RECORDS land, not on
  // every draft change. `draft`/`dispatch` being parameters here instead of
  // outer-scope bindings changes nothing — a param is a closure variable
  // sourced differently. Do not "fix" the dep array.
  useEffect(() => {
    if (!prHistory) return;
    const nextEntries = computeRetroactivePrStamps(draft.entries, prHistory);
    if (!nextEntries) return;
    dispatch({
      type: 'hydrate',
      draft: { ...draft, entries: nextEntries },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prHistory]);

  return {
    prHistory,
    refreshRecords: recordsResource.refresh,
    maxMeta,
    maxesLoaded,
  };
}
